/**
 * Pricing configuration for different countries/currencies
 * Format: cents/rappen (e.g., 499 = 4.99 EUR)
 */

export type CountryCode = string;

export interface PriceConfig {
  countryCode: CountryCode;
  currency: string;
  centAmount: number;
  displayPrice: string;
  isoCountryCodes: string[]; // List of ISO 3166-1 alpha-2 country codes
}

// Default hardcoded prices (used as fallback when no DB price is set)
export const PRICE_CONFIGS: PriceConfig[] = [
  {
    countryCode: 'de',
    currency: 'EUR',
    centAmount: 499,
    displayPrice: '4.99 EUR',
    isoCountryCodes: ['DE'],
  },
  {
    countryCode: 'at',
    currency: 'EUR',
    centAmount: 499,
    displayPrice: '4.99 EUR',
    isoCountryCodes: ['AT'],
  },
  {
    countryCode: 'ch',
    currency: 'CHF',
    centAmount: 400,
    displayPrice: '4.00 CHF',
    isoCountryCodes: ['CH'],
  },
  {
    countryCode: 'fr',
    currency: 'EUR',
    centAmount: 499,
    displayPrice: '4.99 EUR',
    isoCountryCodes: ['FR'],
  },
  {
    countryCode: 'it',
    currency: 'EUR',
    centAmount: 499,
    displayPrice: '4.99 EUR',
    isoCountryCodes: ['IT'],
  },
  {
    countryCode: 'nl',
    currency: 'EUR',
    centAmount: 499,
    displayPrice: '4.99 EUR',
    isoCountryCodes: ['NL'],
  },
  {
    countryCode: 'be',
    currency: 'EUR',
    centAmount: 499,
    displayPrice: '4.99 EUR',
    isoCountryCodes: ['BE'],
  },
  {
    countryCode: 'gb',
    currency: 'GBP',
    centAmount: 399,
    displayPrice: '3.99 GBP',
    isoCountryCodes: ['GB', 'UK'],
  },
  {
    countryCode: 'us',
    currency: 'USD',
    centAmount: 399,
    displayPrice: '3.99 USD',
    isoCountryCodes: ['US'],
  },
  // Default fallback
  {
    countryCode: 'default',
    currency: 'EUR',
    centAmount: 499,
    displayPrice: '4.99 EUR',
    isoCountryCodes: [],
  },
];

/**
 * Ratios of each currency relative to EUR base price.
 * Derived from the default PRICE_CONFIGS (CHF 4.00 / EUR 4.99, etc.)
 */
const CURRENCY_RATIOS_TO_EUR: Record<string, number> = {
  EUR: 1.0,
  CHF: 400 / 499, // ~0.8016
  GBP: 399 / 499, // ~0.7996
  USD: 399 / 499, // ~0.7996
};

/**
 * Computes all price configs from a EUR base price in cents.
 * Other currencies are scaled proportionally and rounded to the nearest cent.
 *
 * Example: baseEurCents = 299 (€2.99)
 *   CHF → round(299 * 0.8016) = 240 cents = CHF 2.40
 *   GBP → round(299 * 0.7996) = 239 cents = £2.39
 *   USD → round(299 * 0.7996) = 239 cents = $2.39
 */
export function computePricesFromBaseEur(baseEurCents: number): PriceConfig[] {
  const eurCents = Math.round(baseEurCents);
  return PRICE_CONFIGS.map(config => {
    const ratio = CURRENCY_RATIOS_TO_EUR[config.currency];
    if (ratio === undefined) return config;
    const centAmount = Math.round(eurCents * ratio);
    const displayAmount = (centAmount / 100).toFixed(2);
    return {
      ...config,
      centAmount,
      displayPrice: `${displayAmount} ${config.currency}`,
    };
  });
}

export function getPriceConfigByCountryCode(
  countryCode: string | null,
  configs: PriceConfig[] = PRICE_CONFIGS,
): PriceConfig {
  if (!countryCode) {
    return configs.find(c => c.countryCode === 'de') || configs.find(c => c.countryCode === 'default')!;
  }

  const normalized = countryCode.toLowerCase();
  const config = configs.find(
    c => c.countryCode === normalized || c.isoCountryCodes.includes(normalized.toUpperCase()),
  );

  return config || (configs.find(c => c.countryCode === 'de') || configs.find(c => c.countryCode === 'default')!);
}

export function getPriceConfigByCurrency(
  currency: string,
  configs: PriceConfig[] = PRICE_CONFIGS,
): PriceConfig | null {
  const normalized = currency.toUpperCase();
  return configs.find(c => c.currency === normalized) || null;
}

export function isValidPriceAmount(
  amount: number,
  currency: string,
  configs: PriceConfig[] = PRICE_CONFIGS,
): boolean {
  const config = getPriceConfigByCurrency(currency, configs);
  if (!config) return false;
  return amount === config.centAmount;
}
